/**
 * Space under the type floor, for the block tools' `finalize`.
 *
 * Where the floor lifts body text ({@link spaceLift}), spacing is lifted by the same factor, so gaps and insets keep their proportion to
 * the text. Down the page (a column's gaps, the space between wrapped lines, top and bottom padding) the lift is whole, since a page unit
 * grows taller; a stage unit and a box of fixed px height cannot, so nothing in them is lifted down. Across it (a row's gaps, side
 * padding) a narrow unit has no width to spare, so each unit takes the largest lift at which every box's content fits as well as it did
 * unlifted. No step is lifted past the default panel padding (s3) lifted, so in a narrow unit the larger steps converge on it (where the
 * lift is whole, s4 equals s3 from about 0.46 m down; s5 keeps its share). Not lifted: a button's or chip's padding (the control's own
 * size; growing it wraps button rows), a rim's (a border's width) and the unit's side padding (the page margin).
 */
import { space, spaceLift } from './design-tokens';

/** What the lift reads of a block. */
export interface LiftBlock {
    kind: string;
    dir?: 'X' | 'Y';
    wrap?: boolean;
    unitKind?: string;
    width?: number;
    fixedPx?: { h?: number };
}

/** What the lift reads of the build being sized. Each is called when a size is asked for, so it may close over later sizing code. */
export interface LiftLayout<B extends LiftBlock> {
    units: readonly B[];
    unitOf(b: B): B;
    items(b: B): B[];
    ancestors(b: B): B[];
    /** The boxes of a unit whose width the layout assigns (not ones that hug their content). */
    boxesOf(unit: B): B[];
    /** Padding that is not spacing: a button's or chip's, a rim's. */
    fixedPad(b: B): boolean;
    gapBase(b: B, unitW: number): number;
    padBase(b: B, unitW: number): number;
    widthOf(b: B): number;
    innerOf(b: B): number;
    /** Drop the widths computed so far: they depend on the lift. */
    resetWidths(): void;
    /** A text's longest word, set. */
    wordWidth(text: B, unitW: number): number;
    svgWidth(svg: B): number;
    itemMin(row: B, unitW: number): number;
    /** A horizontal stack's items as they hug their labels, the widths it wraps by; null when one of them does not hug. */
    hugWidths(stack: B, unitW: number): number[] | null;
}

const round6 = (v: number) => Math.round(v * 1e6) / 1e6;

export function createSpaceLift<B extends LiftBlock>(l: LiftLayout<B>) {
    const across = new Map<B, number>();
    const lifted = (m: number, by: number, unitW: number) => round6(Math.max(m, Math.min(m * by, space('s3', unitW) * by)));
    const fixedHeight = (b: B) => l.unitOf(b).unitKind === 'stage' || [b, ...l.ancestors(b)].some(x => x.fixedPx?.h !== undefined);
    const downBy = (b: B, unitW: number) => l.fixedPad(b) || fixedHeight(b) ? 1 : spaceLift(unitW);
    const acrossBy = (b: B) => l.fixedPad(b) ? 1 : across.get(l.unitOf(b)) ?? 1;
    const gapOf = (b: B, unitW: number) => lifted(l.gapBase(b, unitW), b.dir === 'Y' ? downBy(b, unitW) : acrossBy(b), unitW);
    const padOf = (b: B, unitW: number) => b.kind === 'unit' ? l.padBase(b, unitW) : lifted(l.padBase(b, unitW), acrossBy(b), unitW);

    // A box's narrowest content (its longest word, a one-line row's items side by side, plus its padding), measured against its width.
    // A row that holds its items on one line unlifted keeps them on one line; one that wraps may wrap further. A box that already
    // overflowed unlifted may not overflow further.
    const oneLine = new Map<B, boolean>();
    const leastWidth = (b: B): number => {
        const unitW = l.unitOf(b).width!;
        if (b.kind === 'text') return l.wordWidth(b, unitW);
        if (b.kind === 'svg') return l.svgWidth(b);
        const items = l.items(b);
        if (!items.length || b.kind === 'image' || b.kind === 'stage' || b.kind === 'overlay') return 0;
        const hugs = b.kind === 'stack' && b.dir === 'X' ? l.hugWidths(b, unitW) : null;
        const each = hugs ?? items.map(x => b.kind === 'row' && b.wrap && x.kind !== 'button' && x.kind !== 'chip' && x.kind !== 'svg'
            ? Math.max(leastWidth(x), l.itemMin(b, unitW)) : leastWidth(x));
        const line = each.reduce((a, w) => a + w, 0) + gapOf(b, unitW) * (items.length - 1);
        // A row wraps by its `wrap`; a stack of labels wraps when they overflow it (`hugs`).
        if (!oneLine.has(b)) oneLine.set(b, b.dir === 'X' && (!(b.wrap || hugs) || line <= l.innerOf(b)));
        return (oneLine.get(b) ? line : Math.max(...each)) + 2 * padOf(b, unitW);
    };
    const slack = (u: B): Map<B, number> => {
        l.resetWidths();
        return new Map(l.boxesOf(u).map(b => [b, l.widthOf(b) - leastWidth(b)]));
    };

    return {
        gapOf,
        crossGapOf: (b: B, unitW: number) => lifted(l.gapBase(b, unitW), b.dir === 'Y' ? acrossBy(b) : downBy(b, unitW), unitW),
        padOf,
        padDownOf: (b: B, unitW: number) => lifted(l.padBase(b, unitW), downBy(b, unitW), unitW),
        /** Set each unit's lift across: the whole lift where it fits, else the largest that does (bisection). */
        settle(): void {
            for (const u of l.units) {
                const unlifted = slack(u);
                const fits = (by: number): boolean => {
                    across.set(u, by);
                    return [...slack(u)].every(([b, v]) => v >= Math.min(0, unlifted.get(b)!));
                };
                let [lo, hi] = [1, spaceLift(u.width!)];
                if (!fits(hi)) {
                    for (let i = 0; i < 10; i++) { const mid = (lo + hi) / 2; if (fits(mid)) lo = mid; else hi = mid; }
                    across.set(u, lo);
                }
            }
            l.resetWidths();
        },
    };
}
